/* eslint-disable react/prop-types */
import React, { useCallback, useState } from 'react';
import {
  AFPTableRowAction,
  Alert,
  Modal,
  Button,
  SelectDropdown,
} from '@gsa/afp-component-library';

import { useContractLine } from '../provider/contract-line-provider';
import {
  ReadyIcon,
  TableExpanderCell,
  TextInputField,
  NumberInputField,
  SelectDropdownField,
  TextareaInputField,
} from './ec-components';
import {
  INPUT_TYPE_CODE,
  INPUT_DROPDOWN_OTHER_OPTION,
  INPUT_DROPDOWN_DEFAULT_OPTION,
  ASSOCIATION_TYPE_CODE,
  EC_OPTION_TYPE,
  isInactiveOptionType,
  EC_QUANTITY_REQUIRED_LIST,
  hasConflicts,
  getConflictDependencies,
  isPartOfConflicts,
  getNumericalMinimumLine,
} from './ec-helpers';
import { enDashUnicode } from '../../../../utilities/constants';
import { ConflictsDisplayBlock } from '../optional-equipment/conflicts-components';

const ACTION_CONFLICT = 'Add / edit conflicts';
const actException = {
  icon: 'edit',
  label: 'Add / edit technical exception',
};
const actClarification = {
  icon: 'edit',
  label: 'Add / edit clarification',
};
const actConflict = {
  icon: 'edit',
  label: ACTION_CONFLICT,
};

const ecTitleCell = ({ row: { original } }) => {
  if (original.ecIsNew)
    return (
      <div>
        {original.equipment}{' '}
        <span className="bg-gold text-white font-sans-3xs padding-x-05 padding-y-2px radius-md">
          NEW
        </span>
      </div>
    );
  return original.equipment;
};

/** Minimum Requirements tab */
const MinimumRequirementsCell = ({ original }) => {
  const { associationText } = original;
  const numberLine = getNumericalMinimumLine(original);
  return (
    <div>
      {associationText ? <div>{associationText}</div> : null}
      {numberLine ? <div>{numberLine}</div> : null}
    </div>
  );
};

const VehicleSpecCell = ({ original, onUpdate, readOnly }) => {
  const { inputTypeCode } = original;
  const options = [INPUT_DROPDOWN_DEFAULT_OPTION];
  const { canUpdateContract, isAwardedInFleet } = useContractLine();

  // always return Free text input for legacy contracts
  if (!isAwardedInFleet) {
    return (
      <TextInputField
        original={original}
        onUpdate={onUpdate}
        field="inputValue"
        onUpdateField={inputTypeCode}
        id="text-input"
        label="Vehicle specifications"
        hint={original.ecNotProvided ? 'Not provided' : ''}
        hideLabel
        maxLength={255}
        readonly={readOnly}
      />
    );
  }

  switch (inputTypeCode) {
    case INPUT_TYPE_CODE.DROPDOWN:
      original.preDefinedValue?.value.forEach((value) =>
        options.push({ label: value, value }),
      );
      options.push(INPUT_DROPDOWN_OTHER_OPTION);
      return (
        <SelectDropdownField
          original={original}
          onUpdate={onUpdate}
          options={options}
          field="inputValue"
          onUpdateField={inputTypeCode}
          id="pre-defined-value"
          label="Vehicle specifications"
          hideLabel
          required
          disabled={!canUpdateContract}
          readonly={readOnly}
        />
      );
    case INPUT_TYPE_CODE.COMPLY:
      options.push(
        { label: 'Comply', value: 'Y' },
        { label: 'Not comply', value: 'N' },
      );
      return (
        <SelectDropdownField
          original={original}
          onUpdate={onUpdate}
          options={options}
          field="inputValue"
          onUpdateField={inputTypeCode}
          id="select-comply"
          label="Vehicle specifications"
          hideLabel
          required
          disabled={!canUpdateContract}
          readonly={readOnly}
        />
      );
    case INPUT_TYPE_CODE.NUMERICAL:
    case INPUT_TYPE_CODE.RANGE:
      return (
        <NumberInputField
          original={original}
          onUpdate={onUpdate}
          field="inputValue"
          onUpdateField={inputTypeCode}
          id="number-input"
          label="Vehicle specifications"
          hideLabel
          required
          readonly={readOnly}
        />
      );
    default:
      // FREE TEXT
      return (
        <TextInputField
          original={original}
          onUpdate={onUpdate}
          field="inputValue"
          onUpdateField={inputTypeCode}
          id="text-input"
          label="Vehicle specifications"
          hideLabel
          maxLength={255}
          required
          readonly={readOnly}
        />
      );
  }
};

const ComplyCell = ({ original, onUpdate, isReadOnly = false }) => {
  const { isAwardedInFleet } = useContractLine();
  const options = [
    { label: enDashUnicode, value: '' },
    { label: 'Yes', value: 'Y' },
    { label: 'No', value: 'N' },
  ];
  const disabled =
    original.inputTypeCode && original.inputTypeCode !== INPUT_TYPE_CODE.TEXT;

  return (
    <SelectDropdownField
      original={original}
      onUpdate={onUpdate}
      options={options}
      field="comply"
      id="comply"
      label="Comply"
      required={isAwardedInFleet}
      hideLabel
      disabled={disabled}
      readonly={isReadOnly}
    />
  );
};

const MinReqActionsCell = ({ row }) => {
  const { getStandardEC, setStandardEC, canUpdateContract } = useContractLine();

  const original = getStandardEC(row.original);

  const actions =
    original.comply.newValue === 'N'
      ? [actException, actClarification]
      : [actClarification];

  if (!canUpdateContract) return null;

  return (
    <AFPTableRowAction
      row={row}
      actions={actions}
      onSelectAction={() => {
        if (!row.original.isExpanded)
          setStandardEC(row.original, { table: { isExpanded: true } });
      }}
    />
  );
};

export const getMinReqColumns = (onUpdate, onActionClick, readOnly) => {
  return [
    {
      // Controlled expander cell
      id: 'expander', // needs an ID w/o Header
      sortable: false,
      headerClassName: 'cell-expander',
      Cell: ({ row }) => {
        const { setStandardEC } = useContractLine();
        return (
          <div style={{ padding: 0 }}>
            <TableExpanderCell
              row={row}
              onClick={(isExpanded) => {
                setStandardEC(row.original, { table: { isExpanded } });
              }}
              isReadyOnly={readOnly}
            />
          </div>
        );
      },
    },
    {
      Header: 'Equipment Code - Title',
      accessor: 'equipment',
      Cell: ecTitleCell,
      sortable: false,
    },
    {
      Header: 'Minimum requirements',
      sortable: false,
      Cell: ({ row }) => {
        const { getStandardEC } = useContractLine();
        const original = getStandardEC(row.original);
        return <MinimumRequirementsCell original={original} />;
      },
    },
    {
      Header: 'Vehicle specifications',
      sortable: false,
      Cell: ({ row }) => {
        const { getStandardEC } = useContractLine();
        const original = getStandardEC(row.original);
        return (
          <VehicleSpecCell
            original={original}
            onUpdate={onUpdate}
            readOnly={readOnly}
          />
        );
      },
    },
    {
      Header: 'Comply',
      sortable: false,
      Cell: ({ row }) => {
        const { getStandardEC } = useContractLine();
        const original = getStandardEC(row.original);
        return (
          <ComplyCell
            original={original}
            onUpdate={onUpdate}
            isReadOnly={readOnly}
          />
        );
      },
    },
    {
      Header: 'Complete',
      sortable: false,
      Cell: ({ row }) => {
        const { getStandardEC } = useContractLine();
        const original = getStandardEC(row.original);
        if (original.ecIsNew && original.ready && !original.inputValue.newValue)
          return null;
        return <ReadyIcon isReady={!!original.ready} />;
      },
    },
    {
      Header: 'Actions',
      sortable: false,
      Cell: ({ row }) => (
        <MinReqActionsCell row={row} onActionClick={onActionClick} />
      ),
    },
  ];
};

/** Optional Equipment tab */
const StatusChangeConfirmationModal = ({
  isTurnOff,
  equipment,
  onConfirm,
  onClose,
}) => (
  <Modal
    id="status-change-confirmation-modal"
    className="afp-modal"
    variant="large"
    onClose={onClose}
    actions={
      <>
        <Button
          type="button"
          variant="unstyled"
          onClick={onClose}
          label="Cancel"
        />
        <span className="margin-right-1" />
        <Button
          type="button"
          variant="primary"
          onClick={onConfirm}
          label={isTurnOff ? 'Turn off' : 'Turn on'}
        />
      </>
    }
  >
    <div className="modal-content">
      <h2>Confirmation Needed</h2>
      <p>The selected optional equipment has conflicts associated with it.</p>
      <p>
        <strong>{equipment}</strong>
      </p>
      <p>
        By turning {isTurnOff ? 'off' : 'on'} the equipment code, all associated
        and dependent conflicts will be {isTurnOff ? 'archived' : 'reinstated'}.
        Are you sure you want to turn {isTurnOff ? 'off' : 'on'} this equipment
        code?
      </p>
    </div>
  </Modal>
);

const getStatusCell =
  (onUpdate, readOnly) =>
  ({ row }) => {
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const { getOptionalEC, optionalECs } = useContractLine();
    const original = getOptionalEC(row.original);
    const [selectedValue, setSelectedValue] = useState(
      original.status.newValue,
    );

    const { equipment } = row.original;
    const options = [
      { label: 'On', value: 'On' },
      { label: 'Off', value: 'Off' },
    ];

    const handleUpdateWithConfirmation = useCallback(async () => {
      return new Promise((resolve) => {
        setConfirmAction(() => resolve); // resolve function to be called on confirm
        setShowConfirm(true);
      });
    }, []);

    const onConfirm = useCallback(() => {
      if (confirmAction) {
        confirmAction(true); // resolve the promise with true
        setConfirmAction(null);
      }
      setShowConfirm(false);
    }, [confirmAction]);

    const onCancel = useCallback(() => {
      if (confirmAction) {
        confirmAction(false); // resolve the promise with false
        setConfirmAction(null);
      }
      setShowConfirm(false);
    }, [confirmAction]);

    const onInputUpdate = async ({ target: { value } }) => {
      setSelectedValue(value);
      if (isPartOfConflicts(original, optionalECs)) {
        const isConfirmed = await handleUpdateWithConfirmation();
        if (!isConfirmed) {
          // onCancel - revert update, skip onUpdate call
          setSelectedValue(value === 'On' ? 'Off' : 'On');
          return;
        }
      }
      onUpdate(original, 'status', value);
    };

    if (isInactiveOptionType(original.inputOptionType)) return null;

    if (readOnly) return original.status.newValue;

    return (
      <>
        <SelectDropdown
          name={`${original.id}-status`}
          data-testid={`${original.id}-status`}
          value={selectedValue}
          options={options}
          onChange={onInputUpdate}
        />
        {showConfirm && (
          <div className="status-change-modal-overlay">
            <div className="status-change-modal-content">
              <StatusChangeConfirmationModal
                equipment={equipment}
                onConfirm={onConfirm}
                onClose={onCancel}
                isTurnOff={selectedValue === 'Off'}
              />
            </div>
          </div>
        )}
      </>
    );
  };

const OptEqActionsCell = ({ row, onActionClick }) => {
  const { getOptionalEC, setOptionalEC, canUpdateContract } = useContractLine();

  const original = getOptionalEC(row.original);
  if (isInactiveOptionType(original.inputOptionType)) return null;

  const actions =
    original.inputOptionType.newValue === EC_OPTION_TYPE.standalone
      ? [actConflict, actException, actClarification]
      : [actException, actClarification];
  const onClick = (action) => {
    if (action === ACTION_CONFLICT) onActionClick(action, row);
    else if (!row.original.isExpanded)
      setOptionalEC(row.original, { table: { isExpanded: true } });
  };

  if (!canUpdateContract) return null;

  return (
    <AFPTableRowAction row={row} actions={actions} onSelectAction={onClick} />
  );
};

export const getOptEqColumns = (onUpdate, onActionClick, readOnly) => [
  {
    // Controlled expander cell
    id: 'expander', // needs an ID w/o Header
    sortable: false,
    headerClassName: 'cell-expander',
    Cell: ({ row }) => {
      const { getOptionalEC, setOptionalEC } = useContractLine();
      const original = getOptionalEC(row.original);
      return (
        <div style={{ padding: 0 }}>
          <TableExpanderCell
            row={row}
            onClick={(isExpanded) => {
              setOptionalEC(row.original, { table: { isExpanded } });
            }}
            isHidden={isInactiveOptionType(original.inputOptionType)}
          />
        </div>
      );
    },
  },
  {
    Header: 'Equipment Code - Title',
    accessor: 'equipment',
    Cell: ecTitleCell,
    sortable: false,
  },
  {
    Header: 'Status',
    sortable: false,
    Cell: getStatusCell(onUpdate, readOnly),
  },
  {
    Header: 'Option type',
    sortable: false,
    Cell: ({ row }) => {
      const { getOptionalEC, optionalECs, setAlert } = useContractLine();

      const original = getOptionalEC(row.original);
      const options = [
        { label: '-Select-', value: '' },
        { label: 'Standard', value: EC_OPTION_TYPE.standard },
        { label: 'Standalone', value: EC_OPTION_TYPE.standalone },
        { label: 'Not available', value: EC_OPTION_TYPE.na },
      ];
      const onInputUpdate = useCallback(
        (_original, type, value) => {
          onUpdate(_original, type, value);
        },
        [onUpdate],
      );

      // callback function to determine if option type change is allowed
      const disallowUpdate = (value, showAlert = false) => {
        // disallow update from active to inactive option type if there are conflict dependencies
        if (
          !isInactiveOptionType(original.inputOptionType) &&
          isInactiveOptionType({ newValue: value })
        ) {
          const ecHasConflicts = hasConflicts(original);
          const dependencies = getConflictDependencies(original, optionalECs);
          if (ecHasConflicts || dependencies.length > 0) {
            if (showAlert) {
              setAlert({
                type: 'error',
                message: (
                  <div>
                    <div>
                      Cannot change option type of <b>{original.equipment}</b>{' '}
                      due to conflict dependencies.
                    </div>
                    <div className="padding-left-2">
                      {ecHasConflicts && (
                        <div>
                          Please remove the conflicts associated with{' '}
                          <b>{original.equipment}</b>
                        </div>
                      )}
                      {dependencies.length > 0 && (
                        <div>
                          Please remove <b>{original.equipment}</b> from the
                          conflicts associated with the following equipment:
                          {dependencies.map((dep) => (
                            <li className="margin-left-2" key={dep.equipment}>
                              {dep.categoryTitle}: {dep.equipment}
                            </li>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ),
              });
            }
            return true;
          }
        }

        // disallow update from standalone to standard if ec has conflicts
        if (
          original.inputOptionType.newValue === EC_OPTION_TYPE.standalone &&
          value === EC_OPTION_TYPE.standard &&
          hasConflicts(original)
        ) {
          if (showAlert) {
            setAlert({
              type: 'error',
              message: (
                <div>
                  <div>
                    Cannot change option type of <b>{original.equipment}</b> to
                    Standard due to conflict dependencies.
                  </div>
                  <div className="padding-left-2">
                    Please remove the conflicts associated with{' '}
                    <b>{original.equipment}</b>
                  </div>
                </div>
              ),
            });
          }
          return true;
        }

        // allow change
        return false;
      };

      return (
        <SelectDropdownField
          id="option-type"
          field="inputOptionType"
          label="Option type"
          hideLabel
          required
          original={original}
          onUpdate={onInputUpdate}
          options={options}
          disallowUpdate={disallowUpdate}
          readonly={readOnly}
        />
      );
    },
  },
  {
    Header: 'Max quantity',
    sortable: false,
    Cell: ({ row }) => {
      const { getOptionalEC, isAwardedInFleet } = useContractLine();
      const original = getOptionalEC(row.original);
      const disabled =
        isInactiveOptionType(original.inputOptionType) ||
        !EC_QUANTITY_REQUIRED_LIST.includes(original.quantityRequired);
      const onInputUpdate = useCallback(
        (_original, type, value) => {
          onUpdate(_original, type, value);
        },
        [onUpdate],
      );
      return (
        <NumberInputField
          id="max-quantity"
          field="inputMaxQuantity"
          label="Max quantity"
          hideLabel
          required={isAwardedInFleet}
          disabled={disabled}
          isInt
          original={original}
          onUpdate={onInputUpdate}
          validate={(number) => number > 0}
          readonly={readOnly}
        />
      );
    },
  },
  {
    Header: 'Model year unit price',
    sortable: false,
    Cell: ({ row }) => {
      const { getOptionalEC } = useContractLine();
      const original = getOptionalEC(row.original);
      const disabled =
        isInactiveOptionType(original.inputOptionType) ||
        original.inputOptionType.newValue === EC_OPTION_TYPE.standard;
      const onInputUpdate = useCallback(
        (_original, type, value) => {
          onUpdate(_original, type, value);
        },
        [onUpdate],
      );
      return (
        <NumberInputField
          id="unit-price"
          field="inputUnitPrice"
          label="Unit price"
          hideLabel
          disabled={disabled}
          required
          dataType="currency"
          prefix="$"
          original={original}
          validate={(number) => {
            if (number < -999999)
              return 'Unit price must be greater than -1,000,000';
            if (number > 999999)
              return 'Unit price must be less than 1,000,000';
            if (!Number.isInteger(number))
              return 'Unit price must be a whole number';
            return true;
          }}
          onUpdate={onInputUpdate}
          readonly={readOnly}
        />
      );
    },
  },
  {
    Header: 'Additional shipment days',
    sortable: false,
    Cell: ({ row }) => {
      const { getOptionalEC } = useContractLine();
      const original = getOptionalEC(row.original);
      const disabled = isInactiveOptionType(original.inputOptionType);
      const onInputUpdate = useCallback(
        (_original, type, value) => {
          onUpdate(_original, type, value);
        },
        [onUpdate],
      );
      return (
        <NumberInputField
          id="shipment-days"
          field="inputShipmentDays"
          label="Shipment days"
          hideLabel
          disabled={disabled}
          isInt
          original={original}
          onUpdate={onInputUpdate}
          validate={(number) => number >= 0}
          readonly={readOnly}
        />
      );
    },
  },
  {
    Header: 'Complete',
    sortable: false,
    Cell: ({ row }) => {
      const { getOptionalEC } = useContractLine();
      const original = getOptionalEC(row.original);
      if (
        original.ecIsNew &&
        original.ready &&
        !original.inputOptionType.newValue
      )
        return null;
      return <ReadyIcon isReady={original.ready} />;
    },
  },
  {
    Header: 'Actions',
    sortable: false,
    Cell: ({ row }) => (
      <OptEqActionsCell row={row} onActionClick={onActionClick} />
    ),
  },
];

// Expanded row for both standard and optional EC tables
export const EcExpandedRow = ({
  ecType,
  row,
  onUpdate,
  onActionClick,
  isReadOnly = false,
}) => {
  const { getStandardEC, getOptionalEC } = useContractLine();
  const original =
    ecType === 'S' ? getStandardEC(row.original) : getOptionalEC(row.original);
  const onExplanationUpdate = useCallback(
    (_original, type, value) => {
      onUpdate(_original, type, value);
    },
    [onUpdate],
  );

  const isOptEq =
    original.associationTypeCode === ASSOCIATION_TYPE_CODE.OPTIONAL;
  const isNonComply = !isOptEq && original.comply.newValue === 'N';

  return (
    <div>
      {isNonComply && (
        <div className="margin-top-1 margin-bottom-2">
          {/* dont show alert if isReadOnly */}
          {!isReadOnly && (
            <Alert type="info" slim>
              The entered vehicle specification does not meet the GSA minimum
              requirement. Provide explanation for non-compliance.
            </Alert>
          )}
        </div>
      )}
      <div className="grid-row margin-y-1">
        <div className="grid-col-6 padding-right-2">
          {isNonComply && (
            <div>
              <div className="text-bold">Technical exception</div>
              <TextareaInputField
                original={original}
                onUpdate={onUpdate}
                field="inputException"
                id="exception-textarea"
                label="Technical exception"
                hideLabel
                help="Explanation for non-compliance is optional"
                characterLimit={2000}
                disabled={isReadOnly}
                readonly={isReadOnly}
              />
            </div>
          )}
          {isOptEq && (
            <ConflictsDisplayBlock
              row={row}
              original={original}
              onActionClick={onActionClick}
              isReadOnly={isReadOnly}
              isStandaloneEC={
                original.inputOptionType.newValue === EC_OPTION_TYPE.standalone
              }
            />
          )}
        </div>
        <div className="grid-col-6 padding-left-2">
          {isOptEq && (
            <div className="margin-bottom-2">
              <div className="text-bold">Technical exception</div>
              <TextareaInputField
                original={original}
                onUpdate={onUpdate}
                field="inputException"
                id="exception-textarea"
                label="Technical exception"
                hideLabel
                help="Provide explanation of any non-compliance for this item"
                characterLimit={2000}
                readonly={isReadOnly}
              />
            </div>
          )}
          <div className="text-bold">Clarification</div>
          <TextareaInputField
            original={original}
            onUpdate={onExplanationUpdate}
            field="inputExplanation"
            id="explanation-textarea"
            label="Clarification"
            hideLabel
            help="Provide any additional information about this item"
            characterLimit={2000}
            readonly={isReadOnly}
          />
        </div>
      </div>
    </div>
  );
};
